import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { msalInstance } from '../auth/msalConfig.js';
import { normalizeAccent, type Accent } from './accents.js';
import { fetchPreferences, savePreferences } from './api.js';

export type Theme = 'light' | 'dark' | 'auto';

interface ThemeContextValue {
  /** The chosen appearance preference (light | dark | auto). */
  theme: Theme;
  /** The chosen accent colour scheme. */
  accent: Accent;
  /** The effective appearance after resolving `auto` against the OS preference. */
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  setAccent: (accent: Accent) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Shared keys holding the most recent preferences (drive the pre-paint script in index.html). */
const THEME_KEY = 'catalyst:theme';
const ACCENT_KEY = 'catalyst:accent';

const RELOAD_BASE_DELAY_MS = 1_000;
const RELOAD_MAX_DELAY_MS = 30_000;

/** Per-member storage suffix so each signed-in account keeps its own preference. */
function memberSuffix(): string | null {
  const account = msalInstance?.getActiveAccount() ?? msalInstance?.getAllAccounts()[0];
  return account ? account.homeAccountId : null;
}

function readStored(baseKey: string): string | null {
  try {
    const suffix = memberSuffix();
    return (
      (suffix ? localStorage.getItem(`${baseKey}:${suffix}`) : null) ?? localStorage.getItem(baseKey)
    );
  } catch {
    return null;
  }
}

function writeStored(baseKey: string, value: string): void {
  try {
    localStorage.setItem(baseKey, value);
    const suffix = memberSuffix();
    if (suffix) localStorage.setItem(`${baseKey}:${suffix}`, value);
  } catch {
    /* localStorage unavailable; the in-memory preference still applies. */
  }
}

function normalizeTheme(value: string | null): Theme {
  return value === 'dark' || value === 'auto' ? value : 'light';
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** Resolves a theme preference to an effective dark/light state. */
function resolveDark(theme: Theme): boolean {
  return theme === 'dark' || (theme === 'auto' && systemPrefersDark());
}

function applyAppearance(theme: Theme): void {
  document.documentElement.classList.toggle('dark', resolveDark(theme));
}

function applyAccent(accent: Accent): void {
  document.documentElement.setAttribute('data-accent', accent);
}

/**
 * Provides the current member's appearance (light/dark/auto) and accent colour scheme.
 * The authoritative copy is stored in the database (per member, so it follows them across
 * devices); a localStorage mirror (shared + per-account) drives the pre-paint script in
 * index.html to avoid a flash of the wrong theme, and paints instantly before the server
 * copy arrives. Changes are written back to the server (debounced).
 *
 * Saving is gated on a *successful* initial load: a failed request must not be mistaken
 * for "this member has no saved preference", or the local default would be written back
 * over the real one.
 */
export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => normalizeTheme(readStored(THEME_KEY)));
  const [accent, setAccentState] = useState<Accent>(() => normalizeAccent(readStored(ACCENT_KEY)));
  const [isDark, setIsDark] = useState<boolean>(() => resolveDark(theme));
  const [hydrated, setHydrated] = useState(false);
  const lastSyncedRef = useRef<string>('');
  /** Set when the member picks a theme before the server copy lands, so we don't overwrite it. */
  const dirtyBeforeHydrationRef = useRef(false);

  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  // The API client returns no token until MSAL has settled on an account; requesting
  // earlier just yields a 401 that would otherwise be mistaken for "no saved preference".
  const authReady = isAuthenticated && inProgress === InteractionStatus.None;

  useEffect(() => {
    applyAppearance(theme);
    setIsDark(resolveDark(theme));
  }, [theme]);

  useEffect(() => {
    applyAccent(accent);
  }, [accent]);

  // Load the authoritative server copy, retrying with backoff. `hydrated` flips only on a
  // real response, so a failure leaves saving disabled rather than clobbering the server.
  useEffect(() => {
    if (!authReady || hydrated) return undefined;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = (): void => {
      void fetchPreferences().then((prefs) => {
        if (cancelled) return;
        if (!prefs) {
          attempt += 1;
          timer = setTimeout(
            load,
            Math.min(RELOAD_MAX_DELAY_MS, RELOAD_BASE_DELAY_MS * 2 ** attempt),
          );
          return;
        }
        // A choice made while the request was in flight wins — it's the newer intent.
        if (!dirtyBeforeHydrationRef.current) {
          const nextTheme = prefs.theme ? normalizeTheme(prefs.theme) : theme;
          const nextAccent = prefs.accent ? normalizeAccent(prefs.accent) : accent;
          if (prefs.theme) {
            setThemeState(nextTheme);
            writeStored(THEME_KEY, nextTheme);
          }
          if (prefs.accent) {
            setAccentState(nextAccent);
            writeStored(ACCENT_KEY, nextAccent);
          }
          // Whatever the server already agrees with — don't echo it straight back.
          lastSyncedRef.current = `${nextTheme}|${nextAccent}`;
        }
        setHydrated(true);
      });
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // `theme`/`accent` are read only as the fallback when the server has no value stored;
    // re-running on every change would restart the load, so they're intentionally omitted.
  }, [authReady, hydrated]);

  // Persist changes to the server (debounced) once the initial load has succeeded, and
  // only when the value actually differs from what the server last had.
  useEffect(() => {
    if (!hydrated) return undefined;
    const key = `${theme}|${accent}`;
    if (key === lastSyncedRef.current) return undefined;
    const timer = setTimeout(() => {
      lastSyncedRef.current = key;
      void savePreferences({ theme, accent });
    }, 600);
    return () => clearTimeout(timer);
  }, [theme, accent, hydrated]);

  // Follow the OS preference live while in auto mode.
  useEffect(() => {
    if (theme !== 'auto') return undefined;
    let media: MediaQueryList;
    try {
      media = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return undefined;
    }
    const onChange = (): void => {
      applyAppearance('auto');
      setIsDark(systemPrefersDark());
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  const hydratedRef = useRef(false);
  hydratedRef.current = hydrated;

  const setTheme = useCallback((next: Theme): void => {
    if (!hydratedRef.current) dirtyBeforeHydrationRef.current = true;
    setThemeState(next);
    writeStored(THEME_KEY, next);
  }, []);

  const setAccent = useCallback((next: Accent): void => {
    if (!hydratedRef.current) dirtyBeforeHydrationRef.current = true;
    setAccentState(next);
    writeStored(ACCENT_KEY, next);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, accent, isDark, setTheme, setAccent }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
